import React, { useMemo } from 'react';
import { RadioGroup as RACRadioGroup } from 'react-aria-components';

import { Size } from '../../types';
import {
  FieldLabel,
  FieldMessage,
  focusTarget,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';
import { RadioGroupStyleContext } from './group-context';

/**
 * ComplyKit RadioGroup: React Aria Components RadioGroup + Tailwind v4.
 * Props are fully abstracted from the underlying library.
 *
 * Owns the single selection of the ComplyKit Radios rendered inside it; each
 * child Radio identifies itself through its `value` prop and reads the group
 * state from context, so it needs no isSelected/onChange of its own.
 * Keyboard behaviour is the native pattern: one tab stop, arrow keys move
 * the selection. Its label and message line are a field's (field.md), and
 * the enclosing Form decides whether the label sits above or beside the
 * choices.
 */
interface RadioGroupCommonProps {
  /** The Radios of the group */
  children?: React.ReactNode;
  /**
   * Help under the group: the rule the choice must meet, or why the group is
   * read-only. Inline content only. Hidden while `error` shows.
   */
  description?: React.ReactNode;
  /**
   * The error message. The group is invalid exactly when this is non-empty.
   * Say what to do, restating the rule: "Choose a review cadence".
   */
  error?: string;
  /** The chosen radio's value, or null for none (controlled). */
  value?: string | null;
  /** The value on mount, or null for none (uncontrolled). */
  defaultValue?: string | null;
  /** Called with the chosen value on every change. */
  onChange?: (value: string) => void;
  /** Called when focus leaves the group. */
  onBlur?: (e: React.FocusEvent) => void;
  /** Called when focus enters the group. */
  onFocus?: (e: React.FocusEvent) => void;
  /**
   * Washed out and skipped by Tab. Prefer `isReadOnly` whenever the value
   * still matters to the reader.
   */
  isDisabled?: boolean;
  /**
   * Focusable but not changeable, the marks in neutral. Say why in
   * `description`.
   */
  isReadOnly?: boolean;
  /** Marks the label with an asterisk and exposes `aria-required`. */
  isRequired?: boolean;
  /** The form field name. */
  name?: string;
  /** The id of the group element. */
  id?: string;
  /** Size inherited by the child Radios (their own size prop wins). */
  size?: Size;
  /** Variant inherited by the child Radios (their own variant prop wins). */
  variant?: 'solid' | 'soft' | 'outline';
}

/* The group's children name the choices, never the group itself, so a group
 * needs exactly one name: a visible label above the radios, an aria-label,
 * or the id of the element that names it. The union makes one of them
 * mandatory: an unnamed radiogroup does not compile instead of reading as
 * "group" to a screen reader. */
interface RadioGroupVisiblyLabelledProps extends RadioGroupCommonProps {
  /** The visible name of the group, above the radios. */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface RadioGroupLabelledProps extends RadioGroupCommonProps {
  label?: never;
  /** Accessible name of the group (what choice the radios make together). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface RadioGroupLabelledByProps extends RadioGroupCommonProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the group. */
  'aria-labelledby': string;
}

export type RadioGroupProps =
  | RadioGroupVisiblyLabelledProps
  | RadioGroupLabelledProps
  | RadioGroupLabelledByProps;

/**
 * The forwarded ref is the radio Tab lands on (the chosen one, else the
 * first), so a form library focusing the first invalid field reaches a real
 * input.
 */
export const RadioGroup = React.forwardRef<HTMLInputElement, RadioGroupProps>(function RadioGroup(
  {
    children,
    label,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    size = 'md',
    variant,
    ...props
  },
  ref,
) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(
    ref,
    () => focusTarget(rootRef.current, { checkedFirst: true }) as HTMLInputElement,
  );
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const inherited = useMemo(
    () => ({ size, variant, isReadOnly, inGroup: true }),
    [size, variant, isReadOnly],
  );
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);

  return (
    <RACRadioGroup
      {...props}
      ref={rootRef}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={isInvalid}
      validationBehavior="aria"
      aria-describedby={describedBy}
      className={root()}
      data-component="radio-group"
      data-label-position={labelPosition}
    >
      {label ? (
        <FieldLabel isRequired={isRequired} className={labelClass()}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={column()}>
        <RadioGroupStyleContext.Provider value={inherited}>
          {children}
        </RadioGroupStyleContext.Provider>
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
        />
      </div>
    </RACRadioGroup>
  );
});
